import { StreamClient } from "../streamclient";
import { TelemetryHandler } from "../telemetry/telemetryhandler";
import {
    IEventEmitter,
    IsWindowsOS,
    IsChromeOS,
    IsiDevice,
    IsMacOS,
    PlatformDetails,
    IsLinuxOS,
    IsTizenOS,
    IsAndroidOS,
    IsSafari,
    IsTV,
    Log
} from "../dependencies";
import { GestureDetector, GestureHandler, TouchRecord } from "./gesturedetector";
import { TouchListener } from "./touchlistener";
import {
    EVENTS,
    RNotificationCode,
    StreamingEvent,
    InputConfigFlags,
    Zoneless,
    Hotkey,
    HotkeyModifier
} from "../interfaces";
import { RagnarokProfiler } from "../ragnarokprofiler";
import { VirtualGamepadHandler } from "./virtualgamepad";
import { VIRTUAL_KEYS, CODE_TO_VK_MAP, KEY_TO_VK_MAP, CHAR_TO_VK_MAP } from "./keycodes";
import { RagnarokSettings } from "../util/settings";
import { InputChannel, InputMediaElement, Measurements } from "./inputinterfaces";
import { PacketId, InputModifierFlags, LockKeyBitMask } from "./inputpacketinfo";
import { GamepadHandler } from "./gamepadhandler";
import { LatencyIndicator } from "../debug/latencyindicator";
import { GamepadTester } from "../debug/gamepadtester";
import {
    InputPacketHandler,
    IInputPacketHandlerCallbacks,
    MoveEventBuffer
} from "./inputpackethandler";
import { GamepadRSDMM } from "./gamepadrsdmm";
import { BoundaryPair, IStreamCallbacks, VideoState } from "../rinterfaces";
import { SafeZoneHandler } from "../safezonehandler";
import { MouseHandler, MouseHandlerCallbacks } from "./mousehandler";
import { GetCaptureTimestamp } from "../util/utils";
import { DeviceStateObserver, DeviceStateType } from "../devicestateobserver";
import { InputType, TextCompositionEvent } from "../nskinterfaces";

const LOGTAG = "inputhandler";

// Local interfaces - used in this module only

interface HotkeyLookup {
    localKey?: string; // UIEvent - event.key
    localCode?: VIRTUAL_KEYS; // translated from UIEvent - event.code
    localModifiers: HotkeyModifier;
    upKeys: VIRTUAL_KEYS[];
    sessionKey: VIRTUAL_KEYS;
    sessionModifiers: VIRTUAL_KEYS[];
}

/// Approximate max number of mouse events we want in one packet. This fits a mouse group of 18 packets, 18 more
/// individual ungrouped mouse events, an extra non-move event.
const MAX_MOUSE_EVENTS = 36;

declare interface ResizeObserverSize {
    inlineSize: number;
    blockSize: number;
}

declare interface ResizeObserverEntry {
    target: Element;
    contentRect: DOMRectReadOnly;
    borderBoxSize: readonly ResizeObserverSize[];
    contentBoxSize: readonly ResizeObserverSize[];
    devicePixelContentBoxSize: readonly ResizeObserverSize[];
}

declare class ResizeObserver {
    constructor(
        callback: (entries: readonly ResizeObserverEntry[], observer: ResizeObserver) => void
    );

    observe(target: Element, options?: { box?: "content-box" | "border-box" }): void;
    unobserve(target: Element): void;
    disconnect(): void;
}

declare interface IntersectionObserverInit {
    root?: Element | null;
    rootMargin?: string;
    threshold?: number | number[];
}

declare interface IntersectionObserverEntry {
    readonly boundingClientRect: DOMRectReadOnly;
    readonly intersectionRatio: number;
    readonly intersectionRect: DOMRectReadOnly;
    readonly isIntersecting: boolean;
    readonly rootBounds: DOMRectReadOnly | null;
    readonly target: Element;
    readonly time: number;
}

declare class IntersectionObserver {
    constructor(
        callback: (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => void,
        options?: IntersectionObserverInit
    );

    readonly root: Element | null;
    readonly rootMargin: string;
    readonly thresholds: ReadonlyArray<number>;
    disconnect(): void;
    observe(target: Element): void;
    takeRecords(): IntersectionObserverEntry[];
    unobserve(target: Element): void;
}

const enum MouseButtons {
    LEFT_CLICK = 0,
    MIDDLE_CLICK,
    RIGHT_CLICK,
    MOUSE_FOUR,
    MOUSE_FIVE
}

const enum TextCompositionState {
    COMPOSITION_NONE = 0,
    COMPOSITION_BEGIN = 1,
    COMPOSITION_IN_PROGRESS = 2
}

const MAX_ZOOM_FACTOR = 3;
const MIN_ZOOM_FACTOR = 1.1;

const CHINESE_TRADITIONAL_LAYOUT = "zh-TW";

const DEFAULT_CLEAR_MODIFIERS =
    InputModifierFlags.NVST_MF_META |
    InputModifierFlags.NVST_MF_SHIFT |
    InputModifierFlags.NVST_MF_CONTROL |
    InputModifierFlags.NVST_MF_ALT;

const DEFAULT_HOTKEYS: HotkeyLookup[] = [
    {
        // Map Cmd/Ctrl + Backspace -> Escape
        localCode: VIRTUAL_KEYS.CODE_BACK,
        localModifiers: HotkeyModifier.Default,
        upKeys: getModifierKeysFromInputFlags(DEFAULT_CLEAR_MODIFIERS),
        sessionKey: VIRTUAL_KEYS.CODE_ESCAPE,
        sessionModifiers: []
    },
    {
        // Map Cmd/Ctrl + Delete -> Escape
        localCode: VIRTUAL_KEYS.CODE_DELETE,
        localModifiers: HotkeyModifier.Default,
        upKeys: getModifierKeysFromInputFlags(DEFAULT_CLEAR_MODIFIERS),
        sessionKey: VIRTUAL_KEYS.CODE_ESCAPE,
        sessionModifiers: []
    }
    // // TODO: Decide upon a default letter character to use for Escape.
    // {
    //     localKey: "p",
    //     localModifiers: HotkeyModifier.Default,
    //     upKeys: getModifierKeysFromInputFlags(DEFAULT_CLEAR_MODIFIERS),
    //     sessionKey: VIRTUAL_KEYS.CODE_ESCAPE,
    //     sessionModifiers: []
    // }
    // // Allow <Modifer>+"`" (or equivalent) to inject Escape in-game
    // {
    //     // On most systems, the key to the left of digit 1 is Backquote
    //     // and maps to CODE_OEM_3
    //     localCode: VIRTUAL_KEYS.CODE_OEM_3,
    //     localModifiers: HotkeyModifier.Default,
    //     upKeys: getModifierKeysFromInputFlags(DEFAULT_CLEAR_MODIFIERS),
    //     sessionKey: VIRTUAL_KEYS.CODE_ESCAPE,
    //     sessionModifiers: []
    // },
    // {
    //     // On macOS, the key to the left of digit 1 is IntlBackslash
    //     // and maps to CODE_OEM_BACKSLASH
    //     localCode: VIRTUAL_KEYS.CODE_OEM_BACKSLASH,
    //     localModifiers: HotkeyModifier.Default,
    //     upKeys: getModifierKeysFromInputFlags(DEFAULT_CLEAR_MODIFIERS),
    //     sessionKey: VIRTUAL_KEYS.CODE_ESCAPE,
    //     sessionModifiers: []
    // }
];

export class InputHandler
    implements GestureHandler, IInputPacketHandlerCallbacks, MouseHandlerCallbacks
{
    private videoTagElement: InputMediaElement;
    private videoState: VideoState;
    private keydownFunc: any;
    private keyupFunc: any;
    private focusChangeFunc: any;
    private fullscreenEventFunc: any;
    private preRenderFunc: any;
    private mouseHandler: MouseHandler;
    private gestureDetector?: GestureDetector;
    private touchListener?: TouchListener;
    private eventEmitter: IEventEmitter;
    private rendering: boolean;
    private resizedFunc: any;
    private viewportResizedFunc: any;
    private popStateFunc: any;
    private textInputFunc: any;
    private textCompositionFunc: any;
    private unidentifiedKeys: boolean = false;
    private lastLockKeysState: number;
    private pressedKeys: Set<number> = new Set();
    private supportsNumAndScrollLock: boolean;
    private isChromeOs: boolean;
    private isMacOs: boolean;
    private isAndroidOs: boolean;
    private ignoredLockKeys: number[];
    private telemetry: TelemetryHandler;
    private enabledInputs: InputType = InputType.None;
    private isVirtualKeyboardVisible: boolean = false;
    private enabledInputsBeforeUserIdlePendingOverlay?: InputType;
    private isUserIdleTimeoutPending: boolean;
    private historyProtected: boolean;
    private preventNavigation: boolean;
    private streamClient: StreamClient;
    private perf: PerformanceObserver | null;
    private useVkCodes: boolean = true;
    private windowAddEventListener: any;
    private documentAddEventListener: any;
    private videoAddEventListener: any;
    private windowRemoveEventListener: any;
    private documentRemoveEventListener: any;
    private videoRemoveEventListener: any;
    private gamepadHandler: GamepadHandler;
    private rsdmmHandler: GamepadRSDMM;
    private statsGestureTimerId: number = 0;
    private resizeObserver?: ResizeObserver;
    private intersectionObserver?: IntersectionObserver;
    private touchDelay: TouchDelay = new TouchDelay();
    private gamepadTester: GamepadTester;
    private packetHandler: InputPacketHandler;
    private gamepadTesterEnabled: boolean = false;
    private idleInputListenerFunc: any;
    private textInputElement?: HTMLInputElement;
    private textCompositionState: TextCompositionState = TextCompositionState.COMPOSITION_NONE;
    private textInputDetected: boolean = false;
    private platformDetails: PlatformDetails;
    // TODO: Make this configurable with a remote config option
    private flagTranslateToggleChineseKey: boolean = true;
    private tradChineseLayout: boolean = false;
    private callbacks: IStreamCallbacks;
    private twoFingerTapCount: number = 0;
    private threeFingerTapCount: number = 0;
    private safeZoneHandler: SafeZoneHandler;
    private safeZoneTimeoutId: number = 0;
    private autocapitalizeSetting: string = "off";
    private autocorrectSetting: string = "off";
    private hotkeyKeys: Map<string, HotkeyLookup> = new Map();
    private hotkeyCodes: Map<VIRTUAL_KEYS, HotkeyLookup> = new Map();

    constructor(
        streamClient: StreamClient,
        videoElement: InputMediaElement,
        inputChannel: InputChannel,
        cursorChannel: InputChannel,
        telemetry: TelemetryHandler,
        eventEmitter: IEventEmitter,
        configFlags: InputConfigFlags,
        sendRawTouchInput: boolean,
        gamepadTester: GamepadTester,
        gamepadHandler: GamepadHandler,
        platformDetails: PlatformDetails,
        callbacks: IStreamCallbacks,
        safeZoneHandler: SafeZoneHandler,
        private deviceObserver: DeviceStateObserver,
        measurements: Measurements,
        gamepadChannel?: InputChannel,
        textInputElement?: HTMLInputElement
    ) {
        this.platformDetails = platformDetails;
        this.streamClient = streamClient;
        this.videoTagElement = videoElement;
        this.telemetry = telemetry;
        this.callbacks = callbacks;
        this.safeZoneHandler = safeZoneHandler;

        // Using video element or screen dimensions to start here ensures the cursor's initial position
        // is correct. If we initialize these to zero, the cursor will only be able to start at (0, 0).
        /// TODO: Store our cursor coordinates normalized to 0-65535 so we don't need to change them
        /// when the video dimensions change.
        this.videoState = {
            displayVideoWidth: videoElement.clientWidth || window.screen.width,
            displayVideoHeight: videoElement.clientHeight || window.screen.height,
            scalingFactor: 1,
            leftPadding: 0,
            topPadding: 0,
            videoWidth: 0,
            videoHeight: 0,
            viewportHeight: 0,
            offsetX: 0,
            offsetY: 0,
            zoomFactor: 1
        };

        const zoneless = (window as any).zoneless as Zoneless;
        this.windowAddEventListener =
            zoneless?.windowAddEventListener?.bind(window) ?? window.addEventListener.bind(window);
        this.windowRemoveEventListener =
            zoneless?.windowRemoveEventListener?.bind(window) ??
            window.removeEventListener.bind(window);
        this.documentAddEventListener =
            zoneless?.documentAddEventListener?.bind(document) ??
            document.addEventListener.bind(document);
        this.documentRemoveEventListener =
            zoneless?.documentRemoveEventListener?.bind(document) ??
            document.removeEventListener.bind(document);
        this.videoAddEventListener =
            zoneless?.videoAddEventListener?.bind(videoElement) ??
            zoneless?.documentAddEventListener?.bind(document) ??
            videoElement.addEventListener.bind(videoElement);
        this.videoRemoveEventListener =
            zoneless?.videoRemoveEventListener?.bind(videoElement) ??
            zoneless?.documentRemoveEventListener?.bind(document) ??
            videoElement.removeEventListener.bind(videoElement);

        this.keydownFunc = this.keydown.bind(this);
        this.keyupFunc = this.keyup.bind(this);
        this.fullscreenEventFunc = this.fullscreenEventHandler.bind(this);
        this.preRenderFunc = this.preRender.bind(this);
        this.focusChangeFunc = this.onFocusChange.bind(this);
        this.resizedFunc = this.resized.bind(this);
        this.viewportResizedFunc = this.viewportResized.bind(this);
        this.popStateFunc = this.popstate.bind(this);
        this.idleInputListenerFunc = this.idleInputListener.bind(this);
        this.textInputFunc = this.textInputHandler.bind(this);
        this.textCompositionFunc = this.textCompositionHandler.bind(this);
        this.rendering = false;

        this.gamepadTester = gamepadTester;

        this.preventNavigation = !!configFlags.preventNavigation;

        const moveEventBuffer = new MoveEventBuffer(MAX_MOUSE_EVENTS);
        this.packetHandler = new InputPacketHandler(
            this,
            moveEventBuffer,
            measurements,
            this.videoState,
            this.streamClient,
            inputChannel,
            this.telemetry,
            gamepadChannel
        );
        this.rsdmmHandler = new GamepadRSDMM(this.videoTagElement, this.platformDetails);
        this.mouseHandler = new MouseHandler(
            this,
            streamClient,
            videoElement,
            cursorChannel,
            telemetry,
            configFlags,
            platformDetails,
            this.rsdmmHandler,
            measurements,
            moveEventBuffer,
            this.packetHandler,
            this.deviceObserver
        );

        if (sendRawTouchInput) {
            if (TouchListener.isSupported()) {
                this.touchListener = this.createTouchListener();
                Log.i("{8bacfa5}", "{60f3637}");
            } else {
                Log.w("{8bacfa5}", "{32104d3}");
            }
        } else {
            Log.i("{8bacfa5}", "{6be89bf}");
        }

        if (GestureDetector.isSupported() && !IsTV(this.platformDetails)) {
            this.gestureDetector = this.createGestureDetector();
        }

        this.eventEmitter = eventEmitter;
        // NVST protocol format: https://confluence.nvidia.com/display/GAMESTREAM/NVSC+RemoteInput+protocol

        this.lastLockKeysState = 0;

        this.supportsNumAndScrollLock = IsWindowsOS(this.platformDetails);
        this.isChromeOs = IsChromeOS(this.platformDetails);
        this.isMacOs = IsMacOS(this.platformDetails);
        this.isAndroidOs = IsAndroidOS(this.platformDetails);
        const isSafari = IsSafari(this.platformDetails);

        const windowedMode = !!configFlags.windowedStreaming;
        Log.i("{8bacfa5}", "{fa2e72c}", windowedMode ? "on" : "off");

        // Ignore none, some, or all of the lock keys depending on the platform.
        // If possible, we try to send these through the lock keys state message and ignore the key press.
        // On client platforms that don't keep track of certain key states, send the key press instead.
        if (this.supportsNumAndScrollLock) {
            // Caps lock, num lock, and scroll lock. All lock key states are valid on the client.
            Log.d("{8bacfa5}", "{da5ae1d}");
            this.ignoredLockKeys = [
                VIRTUAL_KEYS.CODE_CAPITAL,
                VIRTUAL_KEYS.CODE_NUMLOCK,
                VIRTUAL_KEYS.CODE_SCROLL
            ];
        } else if (this.isChromeOs || IsLinuxOS(this.platformDetails)) {
            // All lock keys are unsupported or broken here.
            Log.d("{8bacfa5}", "{b7becf9}");
            this.ignoredLockKeys = [];
        } else {
            // Verified on macOS, iPhone, and iPad. Assume support from other platforms.
            Log.d("{8bacfa5}", "{098ce9c}");
            this.ignoredLockKeys = [VIRTUAL_KEYS.CODE_CAPITAL];
        }

        this.isUserIdleTimeoutPending = false;
        this.historyProtected = false;

        if (PerformanceObserver) {
            this.perf = new PerformanceObserver(list => this.perfCallback(list));
        } else {
            this.perf = null;
        }

        this.deviceObserver.addStateListener(DeviceStateType.FULLSCREEN, this.fullscreenEventFunc);

        Log.d("{8bacfa5}", "{cc9c05b}");

        if ((<any>window).ResizeObserver) {
            this.resizeObserver = new ResizeObserver(_entries => {
                this.resized();
            });
            this.resizeObserver.observe(this.videoTagElement);
        } else {
            // Listen for resizes
            this.windowAddEventListener("resize", this.resizedFunc);
        }
        if (
            (IsiDevice(platformDetails) || IsTizenOS(platformDetails)) &&
            (<any>window).IntersectionObserver
        ) {
            this.intersectionObserver = new IntersectionObserver(
                _entries => {
                    this.resized();
                },
                {
                    threshold: [1.0]
                }
            );
            this.intersectionObserver.observe(this.videoTagElement);
        }
        // The resize event on video elements is for the video stream width/height rather than the element
        // width/height, so we need this in addition to ResizeObserver/IntersectionObserver/window resize.
        this.videoTagElement.addEventListener("resize", this.resizedFunc);

        const viewport = (<any>window).visualViewport;
        if (viewport) {
            this.videoState.viewportHeight = viewport.height;
            viewport.addEventListener("resize", this.viewportResizedFunc);
        }

        this.gamepadHandler = gamepadHandler;
        this.gamepadHandler.setGamepadDataSender(this.packetHandler);
        this.packetHandler.addVibrationHandler(this.gamepadHandler);
        this.gamepadHandler.setGamepadRSDMM(this.rsdmmHandler);
        this.gamepadHandler.addTelemetry(this.telemetry);

        // we might already be in fullscreen
        this.fullscreenEventHandler(this.deviceObserver.getFullscreen());
        this.changeFocusHandling(true);
        if (
            this.perf &&
            PerformanceObserver.supportedEntryTypes &&
            PerformanceObserver.supportedEntryTypes.includes("longtask")
        ) {
            this.perf.observe({ entryTypes: ["longtask"] });
        }

        if (RagnarokSettings.latencyTest) {
            LatencyIndicator.getInstance().initialize(this.videoTagElement, this.platformDetails);
        }

        if (IsTV(this.platformDetails) || this.isAndroidOs || IsiDevice(this.platformDetails)) {
            this.textInputElement = textInputElement;

            if (this.isAndroidOs) {
                // Android's system keyboard input is always in IME mode.
                // However, when the user types in English we don't want to wait until the composition is
                // complete but instead send the key strokes directly to the server as the user types.

                // The autocorrect and autocapitalize settings on android interfere in this case and we end
                // up sending every character in caps. Hence turning them off
                this.autocapitalizeSetting =
                    this.textInputElement?.getAttribute("autocapitalize") ?? "off";
                this.autocorrectSetting =
                    this.textInputElement?.getAttribute("autocorrect") ?? "off";

                this.textInputElement?.setAttribute("autocapitalize", "off");
                this.textInputElement?.setAttribute("autocorrect", "off");
            }
        }

        // Only support hotkeys in Safari or windowed mode
        if (
            RagnarokSettings.forceHotkeys ||
            (RagnarokSettings.supportHotkeys && (isSafari || windowedMode))
        ) {
            this.setupHotkeys(configFlags.streamingHotkeys);
            this.logHotkeys();
        }
    }

    uninitialize() {
        this.setUserIdleTimeoutPending(false);
        const videoPaused = this.videoTagElement.paused;
        const videoState =
            videoPaused !== undefined ? (videoPaused ? "paused" : "playing") : "unknown";
        if (this.videoTagElement.paused || (this.videoTagElement.currentTime ?? 1) < 1) {
            this.telemetry.emitDebugEvent(
                "VideoPaused",
                videoState,
                this.videoTagElement.currentTime?.toFixed(2)
            );
        }
        Log.i("{8bacfa5}", "{fdc8bf7}", videoState, this.videoTagElement.currentTime);
        this.intersectionObserver?.disconnect();
        this.resizeObserver?.disconnect();
        this.packetHandler.stop();
        if (this.perf) {
            this.perf.disconnect();
        }
        this.toggleUserInput(false);
        this.windowRemoveEventListener("resize", this.resizedFunc);
        this.videoTagElement.removeEventListener("resize", this.resizedFunc);
        (<any>window).visualViewport?.removeEventListener("resize", this.viewportResizedFunc);

        this.gamepadHandler.disconnectAllGamepads();
        this.packetHandler.removeVibrationHandler(this.gamepadHandler);
        this.deviceObserver.removeStateListener(
            DeviceStateType.FULLSCREEN,
            this.fullscreenEventFunc
        );

        this.rsdmmHandler.stop();
        this.gamepadHandler.removeGamepadDataHandler(this.rsdmmHandler);
        this.gamepadHandler.removeGamepadDataHandler(this.packetHandler);
        if (this.gamepadTesterEnabled) {
            this.gamepadHandler.removeGamepadDataHandler(this.gamepadTester);
            this.gamepadTester.toggleGamepadTester(this.videoTagElement);
        }
        this.mouseHandler.uninitialize();

        this.changeFocusHandling(false);
        this.clearSafeZoneTimeout();
        this.textInputElement?.setAttribute("autocapitalize", this.autocapitalizeSetting);
        this.textInputElement?.setAttribute("autocorrect", this.autocorrectSetting);
        Log.d("{8bacfa5}", "{b6b2756}");
    }

    private createGestureDetector(): GestureDetector {
        const gestureTarget = this.videoTagElement;
        return new GestureDetector(
            gestureTarget,
            this.videoAddEventListener,
            this.videoRemoveEventListener,
            this as GestureHandler
        );
    }

    private createTouchListener(): TouchListener {
        return new TouchListener(
            this.videoTagElement,
            this.videoAddEventListener,
            this.videoRemoveEventListener,
            this.packetHandler,
            this as GestureHandler,
            this.platformDetails
        );
    }

    private changeFocusHandling(enable: boolean) {
        if (enable) {
            this.deviceObserver.addStateListener(DeviceStateType.FOCUS, this.focusChangeFunc);
        } else {
            this.deviceObserver.removeStateListener(DeviceStateType.FOCUS, this.focusChangeFunc);
        }
    }

    // Update the lock keys state on the server.
    // Ignore CapsLock state if specified; only for Traditional Chinese input toggle.
    private updateLockKeysState(evt: KeyboardEvent) {
        // The caps lock key on external keyboards is bugged on ChromeOS.
        // It will always set the caps lock modifier key in the down event and unset it in the up event.
        // So, ignore the modifier keys in this case and send the key directly to the server.
        if (this.isChromeOs && evt.keyCode === VIRTUAL_KEYS.CODE_CAPITAL) {
            return;
        }
        // iOS only sets the CapsLock modifier properly on the caps lock keydown/up.
        if (IsiDevice(this.platformDetails) && evt.keyCode !== VIRTUAL_KEYS.CODE_CAPITAL) {
            return;
        }
        let state = 0;
        // Not all these modifier keys work on all platforms according to:
        // https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/getModifierState
        state |= LockKeyBitMask.NVST_LKB_CAPS_VALID;

        // Must not send Caps Lock state changes when using the key as a toggle between Chinese
        // and Latin input.
        let allowCapsLock: boolean = !this.chineseTogglePermitted();

        if (allowCapsLock && evt.getModifierState("CapsLock")) {
            state |= LockKeyBitMask.NVST_LKB_CAPS;
        }

        if (this.supportsNumAndScrollLock) {
            state |= LockKeyBitMask.NVST_LKB_NUM_VALID;
            state |= LockKeyBitMask.NVST_LKB_SCROLL_VALID;
            if (evt.getModifierState("NumLock")) {
                state |= LockKeyBitMask.NVST_LKB_NUM;
            }
            if (evt.getModifierState("ScrollLock")) {
                state |= LockKeyBitMask.NVST_LKB_SCROLL;
            }
        }

        if (state == this.lastLockKeysState) {
            return;
        }
        this.lastLockKeysState = state;

        this.packetHandler.sendLockKeyState(state);
    }

    public preRender(_timestamp: number) {
        this.mouseHandler.preRender(_timestamp);
    }

    public postRender() {
        this.mouseHandler.postRender();
        this.gamepadHandler.postRender();
    }

    virtualGamepadUpdateHandler() {
        if (this.packetHandler.channelsOpen()) {
            this.gamepadHandler.virtualGamepadUpdateHandler();
        }
    }

    resized(_evt?: Event) {
        this.updateVideoState(this.videoTagElement);

        this.touchListener?.updateVideoState(
            this.videoState,
            this.getMargins(),
            this.videoState.zoomFactor
        );
    }

    viewportResized(evt: Event) {
        this.videoState.viewportHeight = (<any>window).visualViewport!.height;
        this.applyVideoTransforms(
            this.videoState.offsetX,
            this.videoState.offsetY,
            this.videoState.zoomFactor
        );
    }

    popstate(evt: Event) {
        Log.d("{8bacfa5}", "{b6fbd34}");
        history.pushState(null, document.title, location.href);
    }

    private getVirtualKeycode(evt: KeyboardEvent): number {
        if (this.useVkCodes) {
            if (this.isAndroidOs && this.isVirtualKeyboardVisible) {
                // On Android, when text composition is not true, we receive keyDown/KeyUp
                // with valid data for control characters (BackSpace, Enter, etc), but:
                //
                //     softKB inputs are received via KeyboardEvent.key, and KeyboardEvent.code is ""
                // externalKb inputs are received via KeyboardEvent.code as usual.
                //
                // I have not seen the 'evt.key' filled with any values we have defined in KEY_TO_VK_MAP
                // even when entering using korean and chinese.
                //
                // When external KB is connected, isVirtualKeyboardVisible is expected to be false.
                // However, currently UI is setting it to true. So checking for valid code first.
                let vkCode = evt.code?.length
                    ? CODE_TO_VK_MAP.get(evt.code)
                    : CODE_TO_VK_MAP.get(evt.key);
                return vkCode ?? VIRTUAL_KEYS.CODE_NONE;
            } else {
                let vkCode = evt.code ? CODE_TO_VK_MAP.get(evt.code) : KEY_TO_VK_MAP.get(evt.key);
                return vkCode ?? VIRTUAL_KEYS.CODE_NONE;
            }
        } else {
            return evt.keyCode;
        }
    }

    private japaneseSpecialKey(evt: KeyboardEvent) {
        function isKanaKey(key: string) {
            return (
                key == "Hiragana" /* Kana */ ||
                key == "Katakana" /* Shift+Kana */ ||
                key == "Romaji" /* Alt+Kana */ ||
                key == "KanaMode" /* Alt+Kana */
            );
        }
        if (
            evt.code == "CapsLock" &&
            (evt.key == "Alphanumeric" || evt.key == "Katakana" || evt.key == "Hiragana")
        ) {
            // Japanese key Eisu/Caps Lock
            // NOTE:    evt.keyCodes are not 20 (VIRTUAL_KEYS.CODE_CAPITAL) but
            //          240 (Alphanumeric), 241 (Katakana), 242 (Hiragana)
            // Allow through to the session; treat as Caps Lock
            return true;
        } else if (evt.code == "KanaMode" && isKanaKey(evt.key)) {
            // Japanese 106 Hiragana/Katakana (Kana) key (2nd to the right of space) combinations
            return true;
        }
        return false;
    }

    private koreanSpecialKey(evt: KeyboardEvent) {
        // Korean 103 Hangul key (to the right of space).
        return evt.code == "Lang1" || evt.key == "HangulMode";
    }

    private chineseTogglePermitted(): boolean {
        return this.flagTranslateToggleChineseKey && this.isMacOs && this.tradChineseLayout;
    }

    private tradChineseToggleKey(evt: KeyboardEvent): boolean {
        if (!this.chineseTogglePermitted()) {
            return false;
        }
        return evt.code == "CapsLock";
    }

    keydown(evt: KeyboardEvent) {
        let sendGeneratedUpEvent = false;
        this.textInputDetected = false; // tracks if there is a input event between keyDown and keyUp events
        const timeStamp = GetCaptureTimestamp(evt.timeStamp);

        if (!this.deviceObserver.getWindowFocus()) {
            // On Safari, we can receive extra keypresses after we lose focus.
            // Since we release keys on focus lost, this can cause an extra key press to get sent.
            // Ignore all such key presses.
            return;
        }

        // Prevent most browser hotkeys from activating.
        if (this.shouldPreventDefaultKb(evt)) {
            evt.preventDefault();
        }

        let tradCnToggle = this.tradChineseToggleKey(evt);
        if (tradCnToggle) {
            sendGeneratedUpEvent = sendGeneratedUpEvent || tradCnToggle;
        }

        this.updateLockKeysState(evt);

        const jpSpecialKey = this.japaneseSpecialKey(evt);
        sendGeneratedUpEvent = sendGeneratedUpEvent || jpSpecialKey;

        const krSpecialKey = this.koreanSpecialKey(evt);
        sendGeneratedUpEvent = sendGeneratedUpEvent || krSpecialKey;

        // Japanese and Chinese keyboards can use Caps Lock in a different way, including
        // special key combinations, so don't always ignore Caps Lock on those keyboards.
        if (this.ignoredLockKeys.includes(evt.keyCode) && !jpSpecialKey && !tradCnToggle) {
            // On mac when caps lock is enabled only keydown callback is invoked.
            // Upon clearing of the caps lock a keyup is sent for it.
            // To avoid dealing with this, handle lock keys in updateLockKeysState above.
            return;
        }

        const keycode: number = tradCnToggle
            ? VIRTUAL_KEYS.CODE_LSHIFT
            : this.getVirtualKeycode(evt);
        if (keycode === VIRTUAL_KEYS.CODE_NONE) {
            // Any unidentified key would be treated as the composition
            // And gets included in the composition event send back to the client
            // https://nvbugspro.nvidia.com/bug/3965811
            this.unidentifiedKeys = true;
            const compositionEvent: TextCompositionEvent = {
                // Some composition characters could be combined into a new character. For example, Korean.
                // Without special handling, characters are separated instead of getting combined in compositionText field.
                // To better address this, just send out the last character to avoid adding extra complexity here.
                compositionText: evt.key,
                imeRecommendation: true
            };
            this.eventEmitter.emit(EVENTS.TEXT_COMPOSITION, compositionEvent);
            return;
        }
        if (this.unidentifiedKeys) {
            this.unidentifiedKeys = false; // Reset unidentified key state
            const compositionEvent: TextCompositionEvent = { compositionText: "" };
            this.eventEmitter.emit(EVENTS.TEXT_COMPOSITION, compositionEvent);
        }
        if (this.pressedKeys.has(keycode)) {
            return;
        }
        this.pressedKeys.add(keycode);

        let sendEvent: boolean = false;
        const ldat = this.streamClient.getLdatHandler();

        let hotkey = undefined;
        if (keycode == VIRTUAL_KEYS.CODE_OEM_PLUS) {
            // Ignore this event if it was used to send the latency trigger
            sendEvent = !this.streamClient.sendLatencyTrigger();
        } else if (evt.ctrlKey && evt.altKey) {
            // Possible hotkey.
            // Support stutter indicator for explicit override
            if (
                (RagnarokSettings.isInternalUser || RagnarokSettings.stutterIndicator) &&
                (keycode == VIRTUAL_KEYS.CODE_F8 || keycode == VIRTUAL_KEYS.CODE_OEM_4)
            ) {
                // Handle Ctrl+Alt+ (F8/`) key combination.
                this.streamClient.toggleStutterIndicator();
            }
            // Support all other feature toggles for internal users only.
            else if (RagnarokSettings.isInternalUser) {
                switch (keycode) {
                    case VIRTUAL_KEYS.CODE_0:
                        this.mouseHandler.setNextRawUpdate();
                        break;
                    case VIRTUAL_KEYS.CODE_1:
                        this.streamClient.toggleGamepadOverlay();
                        break;
                    case VIRTUAL_KEYS.CODE_8:
                        this.streamClient.toggleGpuViewCapture();
                        break;
                    case VIRTUAL_KEYS.CODE_D:
                        this.streamClient.sendPcmDumpTrigger();
                        break;
                    case VIRTUAL_KEYS.CODE_G:
                        this.gamepadTesterEnabled = !this.gamepadTesterEnabled;
                        this.gamepadTester.toggleGamepadTester(this.videoTagElement);

                        if (this.gamepadTesterEnabled) {
                            this.gamepadHandler.addGamepadDataHandler(this.gamepadTester);
                        } else {
                            this.gamepadHandler.removeGamepadDataHandler(this.gamepadTester);
                        }
                        break;
                    case VIRTUAL_KEYS.CODE_L:
                        ldat?.toggleVisibility();
                        this.mouseHandler.updatePointerLock();
                        break;
                    case VIRTUAL_KEYS.CODE_S:
                        this.safeZoneHandler.toggleDisplaySafeZone();
                        break;
                    case VIRTUAL_KEYS.CODE_F4:
                        this.streamClient.toggleProfiler();
                        break;
                    case VIRTUAL_KEYS.CODE_F5:
                        this.streamClient.toggleWebRTCStats();
                        break;
                    case VIRTUAL_KEYS.CODE_F6:
                        this.streamClient.toggleOnScreenStats(evt.shiftKey);
                        break;
                    case VIRTUAL_KEYS.CODE_F7:
                    case VIRTUAL_KEYS.CODE_OEM_3:
                        this.streamClient.togglePerfIndicator();
                        break;
                    case VIRTUAL_KEYS.CODE_F9:
                        this.mouseHandler.toggleUnadjustedMovement();
                        break;
                    case VIRTUAL_KEYS.CODE_OEM_MINUS:
                        this.useVkCodes = !this.useVkCodes;
                        const message =
                            "Position dependent keys: " + (this.useVkCodes ? "ON" : "OFF");
                        this.streamClient.showDebugMessage(message);
                        Log.i("{8bacfa5}", "{61d2ab5}", (this.useVkCodes ? "ON" : "OFF"));
                        break;
                    case VIRTUAL_KEYS.CODE_M:
                        this.streamClient.sendQosMarker();
                        break;
                    default:
                        sendEvent = true;
                        break;
                }
            } else {
                sendEvent = true;
            }
        } else if ((hotkey = this.getHotkey(evt, keycode))) {
            const baseModifiers = this.getModifierFlags(evt);

            // Clear any existing modifier key downs on server.

            let curModifiers = baseModifiers;
            for (const upkey of hotkey.upKeys) {
                const newModifiers = curModifiers & ~getModifierFlagFromKey(upkey);
                if (curModifiers != newModifiers) {
                    // Removed a modifier from the set, meaning the current
                    // up-key is already pressed.  Release it.
                    curModifiers = newModifiers;
                    this.packetHandler.sendKeyboardEvent(
                        PacketId.PACKET_KEYUP,
                        upkey,
                        curModifiers,
                        timeStamp
                    );
                }
                // Do not consider the modifier keys to be pressed.
                this.pressedKeys.delete(upkey);
            }

            // First, send the appropriate modifier down events.
            for (const modifierkey of hotkey.sessionModifiers) {
                curModifiers |= getModifierFlagFromKey(modifierkey);
                this.packetHandler.sendKeyboardEvent(
                    PacketId.PACKET_KEYDOWN,
                    modifierkey,
                    curModifiers,
                    timeStamp
                );
            }
            // Next send the main hotkey itself as a down, followed by an up.
            this.packetHandler.sendKeyboardEvent(
                PacketId.PACKET_KEYDOWN,
                hotkey.sessionKey,
                curModifiers,
                timeStamp
            );
            this.packetHandler.sendKeyboardEvent(
                PacketId.PACKET_KEYUP,
                hotkey.sessionKey,
                curModifiers,
                timeStamp
            );
            // Finally, send the appropriate modifier up events.
            for (const modifierkey of hotkey.sessionModifiers) {
                curModifiers &= ~getModifierFlagFromKey(modifierkey);
                this.packetHandler.sendKeyboardEvent(
                    PacketId.PACKET_KEYUP,
                    modifierkey,
                    curModifiers,
                    timeStamp
                );
            }

            // Do not consider the key to be pressed..
            this.pressedKeys.delete(keycode);
        } else if (ldat?.isVisible()) {
            if (keycode == VIRTUAL_KEYS.CODE_RETURN) {
                ldat.toggle();
                this.mouseHandler.updatePointerLock();
            } else if (keycode == VIRTUAL_KEYS.CODE_R) {
                ldat.reset();
                this.mouseHandler.updatePointerLock();
            } else if (keycode == VIRTUAL_KEYS.CODE_A) {
                ldat.toggleAutoFire();
            } else if (keycode == VIRTUAL_KEYS.CODE_S) {
                ldat.saveLog();
            } else if (keycode == VIRTUAL_KEYS.CODE_C) {
                ldat.centerLoupe();
            } else if (keycode == VIRTUAL_KEYS.CODE_M) {
                ldat.toggleMoveOnClick();
            } else {
                sendEvent = true;
            }
        } else {
            sendEvent = true;
        }

        if (sendEvent) {
            LatencyIndicator.getInstance().toggleIndicator();
            this.packetHandler.sendKeyboardEvent(
                PacketId.PACKET_KEYDOWN,
                keycode,
                this.getModifierFlags(evt),
                timeStamp
            );
        }

        if (evt.key == "Hankaku" || evt.key == "Zenkaku") {
            // For Japanese keyboards, the 'Backquote' key is effectively a lock state toggle,
            // between Hankaku and Zenkaku. A keydown event is generated when entering each
            // lock state, and keyup when exiting.
            // Therefore, we receive a keyup for one state and keydown for the other, which
            // leaves the key in a down state.
            // If the server is not using a Japanese keyboard, this looks just like auto-repeat,
            // generating lots of (in English US) tilde (~) or backquote (`) characters.

            if (keycode == VIRTUAL_KEYS.CODE_OEM_3) {
                sendGeneratedUpEvent = true;
            }
        }

        if (this.isMacOs && evt.metaKey && !hotkey) {
            // Cmd key sequences on macOS don't generate the keyip event.
            sendGeneratedUpEvent = true;
        }

        if (this.isAndroidOs && this.isVirtualKeyboardVisible) {
            if (evt.code == "Backspace") {
                // For soft keyboard input, evt.code will not be filled.
                // On Android, when external KB is connected and virtualKb is also open, the keyUp for backspace
                // is not getting triggered. This causes either all subsequent backspaces to be ignored or to
                // send backspaces repeatedly if no other key is input immediately.

                sendGeneratedUpEvent = true;
            }

            if (evt.code == "" && evt.key == "Enter") {
                // On Soft keyboard enter, if there is a composition in progress, end it
                const compositionEndEvt: CompositionEvent = new CompositionEvent("compositionend", {
                    data: this.textInputElement?.value
                });
                this.textInputElement?.dispatchEvent(compositionEndEvt);
            }
        }

        if (sendGeneratedUpEvent) {
            this.packetHandler.sendKeyboardEvent(
                PacketId.PACKET_KEYUP,
                keycode,
                this.getModifierFlags(evt),
                timeStamp
            );
            this.pressedKeys.delete(keycode);
        }
    }

    private logHotkeys() {
        for (const hotkeyCode of this.hotkeyCodes.values()) {
            Log.i("{8bacfa5}", "{e7c6e9b}", hotkeyCode.localCode?.toString(
                    16
                ), hotkeyCode.localModifiers?.toString(
                    16
                ), hotkeyCode.sessionKey?.toString(
                    16
                ), hotkeyCode.sessionModifiers?.map(i => i.toString(16)));
        }
        for (const hotkeyKey of this.hotkeyKeys.values()) {
            Log.i("{8bacfa5}", "{b5e3a31}", 
                    hotkeyKey.localKey
                , hotkeyKey.localModifiers?.toString(
                    16
                ), hotkeyKey.sessionKey?.toString(
                    16
                ), hotkeyKey.sessionModifiers?.map(i => i.toString(16)));
        }
    }

    private setupHotkeys(streamingHotkeys?: Hotkey[]) {
        if (!streamingHotkeys) {
            for (const hotkey of DEFAULT_HOTKEYS) {
                if (hotkey.localKey) {
                    this.hotkeyKeys.set(hotkey.localKey, hotkey);
                } else if (hotkey.localCode) {
                    this.hotkeyCodes.set(hotkey.localCode, hotkey);
                }
            }
            return;
        }

        for (const streamingHotkey of streamingHotkeys) {
            // For now, clear all modifiers when sending a hotkey.
            const modifiersToClear = DEFAULT_CLEAR_MODIFIERS;

            const remoteHotkeyCode = streamingHotkey.remote.code;
            if (!remoteHotkeyCode) {
                // If there's not going to be a generated hotkey, just ignore.
                continue;
            }
            const sessionKey = CODE_TO_VK_MAP.get(remoteHotkeyCode);
            if (!sessionKey) {
                // If the mapping is to nothing, just ignore.
                continue;
            }

            const sessionModifiers = getModifierKeysFromModifierFlags(
                streamingHotkey.remote.modifiers
            );

            const localKey = streamingHotkey.local.key;
            const localHotkeyCode = streamingHotkey.local.code;

            if (localHotkeyCode) {
                const localCode = CODE_TO_VK_MAP.get(localHotkeyCode);
                if (!localCode) {
                    continue;
                }
                this.hotkeyCodes.set(localCode, {
                    localCode: localCode,
                    localModifiers: streamingHotkey.local.modifiers,
                    upKeys: getModifierKeysFromInputFlags(modifiersToClear),
                    sessionKey: sessionKey,
                    sessionModifiers: sessionModifiers
                });
            } else if (localKey) {
                this.hotkeyKeys.set(localKey, {
                    localKey: localKey,
                    localModifiers: streamingHotkey.local.modifiers,
                    upKeys: getModifierKeysFromInputFlags(modifiersToClear),
                    sessionKey: sessionKey,
                    sessionModifiers: sessionModifiers
                });
            }
        }
    }

    private getHotkey(evt: KeyboardEvent, code: VIRTUAL_KEYS): HotkeyLookup | undefined {
        if (!(evt.ctrlKey || evt.shiftKey || evt.metaKey || evt.altKey)) {
            // Hotkeys require at least one modifier to be pressed
            return;
        }

        let hotkey = this.hotkeyCodes.get(code);
        if (!hotkey) {
            hotkey = this.hotkeyKeys.get(evt.key);
        }
        if (hotkey) {
            let localModifiers = hotkey.localModifiers;
            if (localModifiers == HotkeyModifier.Default) {
                localModifiers = this.isMacOs ? HotkeyModifier.Meta : HotkeyModifier.Ctrl;
            }
            const ctrlSet = evt.ctrlKey;
            const altSet = evt.altKey;
            const metaSet = evt.metaKey;
            const shiftSet = evt.shiftKey;

            const ctrlNeeded = localModifiers & HotkeyModifier.Ctrl;
            const altNeeded = localModifiers & HotkeyModifier.Alt;
            const metaNeeded = localModifiers & HotkeyModifier.Meta;
            const shiftNeeded = localModifiers & HotkeyModifier.Shift;

            let curMatch =
                (ctrlNeeded ? ctrlSet : !ctrlSet) &&
                (altNeeded ? altSet : !altSet) &&
                (metaNeeded ? metaSet : !metaSet) &&
                (shiftNeeded ? shiftSet : !shiftSet);

            if (curMatch) {
                return hotkey;
            }
        }

        return undefined;
    }

    keyup(evt: KeyboardEvent) {
        let sendGeneratedDownEvent = false;
        const timeStamp = GetCaptureTimestamp(evt.timeStamp);

        if (
            this.isAndroidOs &&
            this.isVirtualKeyboardVisible &&
            evt.code == "" && // this will rule out input from external kb
            evt.key == "Unidentified"
        ) {
            // WAR for Android soft keyboard where backspaces are consumed by the IME compositor
            // If there is no input event between a keyDown and keyUp event, then treat it
            // as backspace and send keycode to server
            if (!this.textInputDetected) {
                this.packetHandler.sendKeyboardEvent(
                    PacketId.PACKET_KEYDOWN,
                    VIRTUAL_KEYS.CODE_BACK,
                    0,
                    timeStamp
                );
                this.packetHandler.sendKeyboardEvent(
                    PacketId.PACKET_KEYUP,
                    VIRTUAL_KEYS.CODE_BACK,
                    0,
                    timeStamp
                );
                return;
            }
        }

        let tradCnToggle = this.tradChineseToggleKey(evt);
        if (tradCnToggle) {
            sendGeneratedDownEvent = sendGeneratedDownEvent || tradCnToggle;
        }

        this.updateLockKeysState(evt);

        if (this.ignoredLockKeys.includes(evt.keyCode) && !tradCnToggle) {
            return;
        }

        const keycode: number = tradCnToggle
            ? VIRTUAL_KEYS.CODE_LSHIFT
            : this.getVirtualKeycode(evt);

        if (sendGeneratedDownEvent) {
            this.packetHandler.sendKeyboardEvent(
                PacketId.PACKET_KEYDOWN,
                keycode,
                this.getModifierFlags(evt),
                timeStamp
            );
            this.pressedKeys.add(keycode);
        }

        if (!this.pressedKeys.has(keycode)) {
            return;
        }
        this.pressedKeys.delete(keycode);

        LatencyIndicator.getInstance().toggleIndicator();
        this.packetHandler.sendKeyboardEvent(
            PacketId.PACKET_KEYUP,
            keycode,
            this.getModifierFlags(evt),
            timeStamp
        );
    }

    clientAspectGreater(client: number, session: number): boolean {
        let diff = Math.abs(client - session);
        if (diff < 0.01) {
            // Close enough to be considered the same.
            return false;
        } else {
            return client > session;
        }
    }

    private updateVideoState(videoElement: InputMediaElement) {
        if (videoElement.videoWidth <= 0 || videoElement.videoHeight <= 0) {
            // No session size yet, so ignore this state update.
            return;
        }

        let scalingFactor = 1.0;
        let topPadding = 0.0;
        let leftPadding = 0.0;
        let videoRatio = videoElement.videoWidth / videoElement.videoHeight;
        let elementRatio = videoElement.clientWidth / videoElement.clientHeight;

        let displayVideoWidth: number;
        let displayVideoHeight: number;

        // There can be padding, either above/below (letterbox) or
        // left/right (PillarBox) of the video (remote session) content.
        // Take ratio of element's actual width(height) and video's real width(height).
        // (Ratio of CSS pixels to server pixels.)
        // That will be the scaling factor for the entire video;
        // the video aspect doesn't change.
        // Use that scaling factor as multiplier for received event co-ordinates,
        // *after* those co-ordinates have been corrected for the padding bars.

        if (this.clientAspectGreater(elementRatio, videoRatio)) {
            // PillarBox
            scalingFactor = videoElement.clientHeight / videoElement.videoHeight;
            displayVideoWidth = videoElement.videoWidth * scalingFactor;
            displayVideoHeight = videoElement.videoHeight * scalingFactor;
            let totalPadding = videoElement.clientWidth - displayVideoWidth;
            leftPadding = totalPadding / 2;
        } else {
            // LetterBox
            scalingFactor = videoElement.clientWidth / videoElement.videoWidth;
            displayVideoWidth = videoElement.videoWidth * scalingFactor;
            displayVideoHeight = videoElement.videoHeight * scalingFactor;
            let totalPadding = videoElement.clientHeight - displayVideoHeight;
            topPadding = totalPadding / 2;
        }

        let horizontalScaleFactor = 1;
        let verticalScaleFactor = 1;

        if (this.videoState.displayVideoWidth && this.videoState.displayVideoHeight) {
            horizontalScaleFactor = displayVideoWidth / this.videoState.displayVideoWidth;
            verticalScaleFactor = displayVideoHeight / this.videoState.displayVideoHeight;
        }

        this.videoState.displayVideoWidth = displayVideoWidth;
        this.videoState.displayVideoHeight = displayVideoHeight;
        this.videoState.scalingFactor = scalingFactor;
        this.videoState.topPadding = topPadding;
        this.videoState.leftPadding = leftPadding;
        this.videoState.videoWidth = videoElement.videoWidth;
        this.videoState.videoHeight = videoElement.videoHeight;
        // Scale translations to the new coordinate space
        this.applyVideoTransforms(
            this.videoState.offsetX * horizontalScaleFactor,
            this.videoState.offsetY * verticalScaleFactor,
            this.videoState.zoomFactor
        );

        this.mouseHandler.updateVideoState(this.videoState);
        this.safeZoneHandler.updateVideoState(
            this.videoState.topPadding,
            this.videoState.leftPadding
        );
        this.sendSafeZone();
    }

    fullscreenEventHandler(fullscreen: boolean) {
        Log.d("{8bacfa5}", "{77df54c}", (fullscreen ? "fullscreen" : "not fullscreen"));

        this.resized({} as Event);

        if (fullscreen) {
            if (window.isSecureContext) {
                const keyboard = (<any>window.navigator).keyboard;
                // Not all browsers support navigator.keyboard and keyboard.lock
                if (keyboard && keyboard.lock) {
                    // Allow Alt+Tab to work as expected.
                    // Lock and capture other keys - sequences (generally Ctrl) with these keys cause issues
                    keyboard.lock([
                        // Leaves fullscreen if entered by our button
                        "Escape",

                        // Leaves fullscreen if entered by browser menu or F11 key (or programmatically, without this lock)
                        "F11",

                        // Navigates browser
                        "BrowserBack",
                        "BrowserForward",
                        "BrowserRefresh",
                        "BrowserHome",
                        "BrowserFavorites",
                        "BrowserSearch",
                        "BrowserStop",
                        // Power control
                        "Sleep",
                        "Power",
                        "WakeUp",

                        // ChromeOS interactions
                        "KeyT", // Ctrl+Alt+T opens Terminal tab on ChromeOS
                        "KeyZ", // Ctrl+Alt+Z opens Chrome spoken feedback (ChromeOS?)
                        "Slash", // Ctrl+Alt+/ opens Keyboard shortcuts on ChromeOS

                        // ChromeOS shelf icons (Alt+<digit> clicks icon on shelf)
                        "Digit1",
                        "Digit2",
                        "Digit3",
                        "Digit4",
                        "Digit5",
                        "Digit6",
                        "Digit7",
                        "Digit8",
                        "Digit9",

                        // ChromeOS System settings, specific apps, etc.
                        "KeyM", // Alt+Shift+M opens Files app, Ctrl+Search+M opens Magnifier
                        "KeyD", // Ctrl+Search+D opens Magnifier
                        "KeyN", // Alt+Shift+N opens notifications
                        "KeyS", // Alt+Shift+S opens status area
                        "KeyK", // Shift+Search+K shows list of input methods/layouts
                        "KeyL", // Alt+Shift+L highlights launcher
                        "Space", // Ctrl+Space/Ctrl+Shift+Space switch to next/previous input method

                        // Generic controls
                        "PrintScreen", // Captures screenshot

                        // Start new apps
                        "LaunchApp1",
                        "LaunchApp2",
                        "LaunchMail",

                        // macOS
                        "Comma", // Cmd+Comma opens app preferences
                        "Semicolon", // Cmd+Shift+Semicolon opens Spelling and grammar

                        "ArrowLeft", // Cmd+Left navigates back
                        "ArrowRight", // Cmd+Right navigates forwards
                        "BracketLeft", // Cmd+[ navigates back
                        "BracketRight", // Cmd+] navigates forwards
                        "KeyW", // Cmd+W closes current Tab
                        "KeyQ", // Cmd+Q closes application (long hold)
                        "KeyR", // Cmd+R refreshes current Tab
                        "KeyY", // Cmd+Y opens History
                        "KeyO", // Cmd+O opens Open File window
                        "KeyP", // Cmd+P opens Print window
                        "KeyF", // Cmd+F opens Find
                        "KeyG" // Cmd+G opens Repeat Find / Find Again
                    ]);
                }
            }

            this.videoTagElement.onclick = null;
            this.videoTagElement.removeAttribute("controls");
        } else {
            // TODO: Fix this causing a double escape key press when leaving fullscreen. When the user holds escape to
            //       exit fullscreen, we release all the pressed keys here (including escape). However, since the key is
            //       still down, Chrome generates more keydowns, and we send another key press. We can probably check
            //       the KeyboardEvent.repeat property.
            //       This doesn't happen in GFN production because we only enable input in fullscreen
            this.releasePressedKeysAndButtons();

            if (window.isSecureContext) {
                const keyboard = (window.navigator as any).keyboard;
                // Not all browsers support navigator.keyboard and keyboard.unlock
                if (keyboard && keyboard.unlock) {
                    keyboard.unlock();
                }
            }
        }
    }

    private enableBackPrevention() {
        if (!this.historyProtected) {
            this.historyProtected = true;
            history.pushState(null, document.title, location.href);
            this.windowAddEventListener("popstate", this.popStateFunc);
        }
    }

    private disableBackPrevention() {
        if (this.historyProtected) {
            this.windowRemoveEventListener("popstate", this.popStateFunc);
            history.back();
            this.historyProtected = false;
        }
    }

    private isTrueTouchActive() {
        return this.touchListener && !this.isVirtualKeyboardVisible;
    }

    private enableUserInput(inputs: InputType): boolean {
        const wereAllDisabled = !this.isUserInputEnabled();
        const addedInputs = (this.enabledInputs ^ inputs) & inputs;
        if (!addedInputs) {
            return false;
        }
        this.enabledInputs |= addedInputs;
        // set once only if all were disabled
        if (wereAllDisabled) {
            this.rendering = true;
        }

        if (addedInputs & InputType.Gamepad) {
            this.gamepadHandler.enableUserInput();
        }

        if (addedInputs & InputType.Mouse) {
            this.mouseHandler.enableUserInput();
        }

        if (addedInputs & InputType.Keyboard) {
            this.documentAddEventListener("keydown", this.keydownFunc);
            this.documentAddEventListener("keyup", this.keyupFunc);

            this.textInputElement?.addEventListener("input", this.textInputFunc);
            this.textInputElement?.addEventListener("compositionstart", this.textCompositionFunc);
            this.textInputElement?.addEventListener("compositionupdate", this.textCompositionFunc);
            this.textInputElement?.addEventListener("compositionend", this.textCompositionFunc);
            this.textCompositionState = TextCompositionState.COMPOSITION_NONE;
        }

        if (addedInputs & InputType.Touch) {
            if (this.isTrueTouchActive()) {
                this.touchListener!.start();
            } else if (this.gestureDetector) {
                this.gestureDetector.start();
            }
        }

        if (wereAllDisabled) {
            // Prevent Back navigation
            if (this.preventNavigation) {
                this.enableBackPrevention();
            }
        }
        return true;
    }

    private disableUserInput(inputs: InputType): boolean {
        const removedInputs = this.enabledInputs & inputs;
        if (!removedInputs) {
            return false;
        }
        this.enabledInputs ^= removedInputs;
        // only reset if all disabled
        const allDisabled = !this.isUserInputEnabled();
        if (allDisabled) {
            this.rendering = false;
        }

        if (removedInputs & InputType.Gamepad) {
            this.gamepadHandler.disableUserInput();
        }

        if (removedInputs & InputType.Mouse) {
            this.mouseHandler.disableUserInput();
        }

        if (removedInputs & InputType.Keyboard) {
            this.releasePressedKeys();
            this.documentRemoveEventListener("keydown", this.keydownFunc);
            this.documentRemoveEventListener("keyup", this.keyupFunc);
            this.textInputElement?.removeEventListener("input", this.textInputFunc);
            this.textInputElement?.removeEventListener(
                "compositionstart",
                this.textCompositionFunc
            );
            this.textInputElement?.removeEventListener(
                "compositionupdate",
                this.textCompositionFunc
            );
            this.textInputElement?.removeEventListener("compositionend", this.textCompositionFunc);
        }

        if (removedInputs & InputType.Touch) {
            if (this.gestureDetector) {
                this.gestureDetector.stop();
            }
            if (this.touchListener) {
                this.touchListener.stop();
            }
            if (this.statsGestureTimerId !== 0) {
                this.clearStatsGestureTimer();
            }
            this.touchDelay.clear();
        }

        if (allDisabled) {
            if (this.preventNavigation) {
                this.disableBackPrevention();
            }
        }
        return true;
    }

    public toggleUserInput(enable: boolean, inputs?: InputType) {
        Log.d("{8bacfa5}", "{2424824}", enable, inputs);
        const changedInputs = inputs ?? InputType.All;
        const state = enable
            ? this.enableUserInput(changedInputs)
            : this.disableUserInput(changedInputs);
    }

    public isUserInputEnabled(): boolean {
        return this.enabledInputs !== InputType.None;
    }

    private onFocusChange(focus: boolean): void {
        if (focus) {
            this.sendSafeZone();
            Log.d("{8bacfa5}", "{cb19d31}");
        } else {
            Log.d("{8bacfa5}", "{8744dbe}");
            this.releasePressedKeysAndButtons();
        }
    }

    public onEnterPointerLock(): void {}

    public onExitPointerLock(sendEscape: boolean): void {
        if (sendEscape) {
            // When the user presses Escape to leave pointer lock while holding another key, Firefox and Chrome won't
            // send keyups for the remaining held keys, leaving them in a down state. Release all such keys.
            // This doesn't affect pressed mouse buttons for some reason.
            // Safari won't reach here because it sends normal escape key down and up events, so we don't need to send
            // one here.
            this.releasePressedKeys();
            Log.i("{8bacfa5}", "{f46c4da}");
            // TODO: We might need to store and include the last modifier key state when sending these. Right now these
            //       will release all modifier keys on the server.
            this.packetHandler.sendKeyboardEvent(
                PacketId.PACKET_KEYDOWN,
                VIRTUAL_KEYS.CODE_ESCAPE,
                0
            );
            this.packetHandler.sendKeyboardEvent(
                PacketId.PACKET_KEYUP,
                VIRTUAL_KEYS.CODE_ESCAPE,
                0
            );
        }
    }

    public isEscapePressed(): boolean {
        return this.pressedKeys.has(VIRTUAL_KEYS.CODE_ESCAPE);
    }

    private getShiftModifierFlag(evt: KeyboardEvent): number {
        let flag = undefined;

        // WAR for iOS.
        // A complete solution requires iOS-specific keyboard layout map, or mapping to a known layout.
        if (IsiDevice(this.platformDetails)) {
            // When a keyboard event is originated from a virtual keyboard,
            // its `shiftKey` might differ from the one typed by a physical keyboard.
            // For instance, "@" is { keyCode: 50, shiftKey: true } on physical keyboard,
            //                      { keyCode: 50, shiftKey: false } on virtual keyboard (tested with iPhone).
            // To inject correct key on the server, we add SHFIT modifier regardless what `shiftKey` is for
            // some characters that must be shifted.
            // Note that this only works when the server is running US keyboard layout map.
            const keysRequiredToBeShifted = '!@#$%^&*()~_+{}|:"<>?';
            // Further, certain characters must not be shifted, so we explicitly remove the SHIFT modifier.
            const keysRequiredToBeUnshifted = "1234567890`-=[]\\;',./";
            if (evt.key.length === 1) {
                if (keysRequiredToBeShifted.includes(evt.key)) {
                    flag = InputModifierFlags.NVST_MF_SHIFT;
                } else if (keysRequiredToBeUnshifted.includes(evt.key)) {
                    flag = InputModifierFlags.NVST_MF_NONE;
                }
            }
        }

        if (flag === undefined && evt.shiftKey && !evt.code.startsWith("Shift")) {
            flag = InputModifierFlags.NVST_MF_SHIFT;
        }
        return flag ?? InputModifierFlags.NVST_MF_NONE;
    }

    private getModifierFlags(evt: KeyboardEvent): number {
        let flags = InputModifierFlags.NVST_MF_NONE;
        if (evt.ctrlKey && !evt.code.startsWith("Control")) {
            flags |= InputModifierFlags.NVST_MF_CONTROL;
        }
        if (evt.altKey && !evt.code.startsWith("Alt")) {
            flags |= InputModifierFlags.NVST_MF_ALT;
        }
        if (evt.metaKey && !evt.code.startsWith("Meta")) {
            flags |= InputModifierFlags.NVST_MF_META;
        }
        flags |= this.getShiftModifierFlag(evt);
        return flags;
    }

    public sendHeartbeatEvent() {
        // Make sure to revert to previous input state.
        if (this.enabledInputsBeforeUserIdlePendingOverlay !== undefined) {
            this.toggleUserInput(
                false,
                ~this.enabledInputsBeforeUserIdlePendingOverlay & InputType.All
            );
            this.enabledInputsBeforeUserIdlePendingOverlay = undefined;
        }

        this.packetHandler.sendHeartbeatEvent();
    }

    private clearIdleTimeout() {
        this.setUserIdleTimeoutPending(false);
        let timerEvent: StreamingEvent = {
            streamingWarnings: {
                code: RNotificationCode.ClearUserIdleTimeOut
            }
        };
        this.eventEmitter.emit(EVENTS.STREAMING_EVENT, timerEvent);
        this.sendHeartbeatEvent();
        this.callbacks.onUserIdleClear();
    }

    private getIdleEvents(): string[] {
        return [this.mouseHandler.getMoveEventName(), "pointerdown", "touchstart"];
    }

    private idleInputListener() {
        if (this.isUserIdleTimeoutPending) {
            this.clearIdleTimeout();
        }
    }

    public setUserIdleTimeoutPending(value: boolean) {
        this.isUserIdleTimeoutPending = value;
        // if input already disabled means we have some overlay already, like go to fullscreen, enable input (if not already) just to
        // listen input for resetting idle timer on server. We could also check if we are in non-fullscreen instead
        // of checking enabledInputs flag. Assumption is client calls toggleUserInput(false) on all popups except userIdleWarning popup.
        if (value && this.enabledInputs !== InputType.All) {
            this.enabledInputsBeforeUserIdlePendingOverlay = this.enabledInputs;
            this.toggleUserInput(true);
        }
        if (value) {
            this.getIdleEvents().forEach(event =>
                this.documentAddEventListener(event, this.idleInputListenerFunc)
            );
        } else {
            // Must clear all idle listeners on false to avoid leaking listeners.
            this.getIdleEvents().forEach(event =>
                this.documentRemoveEventListener(event, this.idleInputListenerFunc)
            );
        }
    }

    releasePressedKeys() {
        if (this.pressedKeys.size > 0) {
            Log.i("{8bacfa5}", "{7998ee8}", this.pressedKeys.size);
            for (const keyCode of this.pressedKeys) {
                this.packetHandler.sendKeyboardEvent(PacketId.PACKET_KEYUP, keyCode, 0);
            }
            this.pressedKeys.clear();
        }
    }

    releasePressedKeysAndButtons() {
        this.releasePressedKeys();
        this.mouseHandler.releasePressedButtons();
    }

    private perfCallback(list: PerformanceObserverEntryList) {
        list.getEntriesByType("longtask").forEach(entry => {
            let duration = Math.round(entry.duration);
            RagnarokProfiler.addMainThreadBlockDuration(duration, entry.startTime);
            this.streamClient.updateBlockedDuration(duration);
            Log.i("{8bacfa5}", "{7071359}", duration);
        });
    }

    getVirtualGamepadHandler(): VirtualGamepadHandler {
        return this.gamepadHandler.getVirtualGamepadHandler();
    }

    public sendTextInput(text: ArrayBuffer) {
        this.packetHandler.sendTextInput(text);
    }

    private sendAndClearText(text: string) {
        const encoder = new TextEncoder();
        const unicodeText = encoder.encode(text);
        this.sendTextInput(unicodeText.buffer);

        this.textInputElement!.value = "";
    }

    private hasVirtualKeyCodeMapping(text: String): boolean {
        for (let i = text.length - 1; i >= 0; i--) {
            if (CHAR_TO_VK_MAP.get(text.charAt(i)) === undefined) {
                return false;
            }
        }
        return true;
    }

    private sendCharCodesAndClearText(text: string) {
        // On Android, the NVST_LKB_CAPS is not being set for softKB but it gets set for external kb.
        // We use this.lastLockKeysState to determine the CAPS lock state on the server and adjust the
        // ShiftModifier accordingly.
        let isCapsLockOn = false;
        if (this.lastLockKeysState & LockKeyBitMask.NVST_LKB_CAPS_VALID) {
            isCapsLockOn = (LockKeyBitMask.NVST_LKB_CAPS & this.lastLockKeysState) != 0;
        }

        // If we are in this function, it is guaranteed that the input text is comprised of char codes between
        // 32 and 126 (printable chars in the ascii charset). Process the text char-by-char and send the
        // key codes directly to the server.
        for (let i = 0; i < text.length; i++) {
            const vkmap = CHAR_TO_VK_MAP.get(text.charAt(i));
            if (!vkmap) {
                Log.e("{8bacfa5}", "{67554be}", text.charAt(i));
                return;
            }

            const keycode = vkmap!.vkCode;
            let flags = vkmap!.shift
                ? InputModifierFlags.NVST_MF_SHIFT
                : InputModifierFlags.NVST_MF_NONE;

            if (keycode >= VIRTUAL_KEYS.CODE_A && keycode <= VIRTUAL_KEYS.CODE_Z) {
                // Only CAPITAL letter keycodes are sent to server (See CODE_TO_VK_MAP in keycodes.ts).
                // Determine the shift modifier based on the CAPS lock state on the server
                if (isCapsLockOn) {
                    flags = ~flags;
                }
            }
            // If shift modifier is to be applied, send the LSHIFT keydown and keyup
            if (flags) {
                this.packetHandler.sendKeyboardEvent(
                    PacketId.PACKET_KEYDOWN,
                    VIRTUAL_KEYS.CODE_LSHIFT,
                    0,
                    performance.now()
                );
            }
            this.packetHandler.sendKeyboardEvent(
                PacketId.PACKET_KEYDOWN,
                keycode,
                flags,
                performance.now()
            );
            this.packetHandler.sendKeyboardEvent(
                PacketId.PACKET_KEYUP,
                keycode,
                flags,
                performance.now()
            );
            if (flags) {
                this.packetHandler.sendKeyboardEvent(
                    PacketId.PACKET_KEYUP,
                    VIRTUAL_KEYS.CODE_LSHIFT,
                    0,
                    performance.now()
                );
            }
        }
        // This will clear any composition that is underway
        this.textInputElement!.value = "";
    }

    private textCompositionHandler(e: CompositionEvent) {
        switch (e.type) {
            case "compositionstart":
                this.textCompositionState = TextCompositionState.COMPOSITION_BEGIN;
                break;
            case "compositionupdate":
                {
                    // For iOS, composition events occur only for IME langs
                    // For other platforms, when when non-ascii inputs are detected, set IME recommendation
                    if (IsiDevice(this.platformDetails) || !this.hasVirtualKeyCodeMapping(e.data)) {
                        const compositionEvent: TextCompositionEvent = {
                            compositionText: e.data,
                            imeRecommendation: true
                        };
                        this.eventEmitter.emit(EVENTS.TEXT_COMPOSITION, compositionEvent);
                    }
                }
                break;
            case "compositionend":
                // Trim the extra space at the end ONLY for Android to not alter the current behaviour but we
                // need to revist this. Having extra space while entering passwords will be incorrect.
                const inputText = this.isAndroidOs ? e.data.trim() : e.data;
                if (inputText.length) {
                    const imeMode =
                        IsiDevice(this.platformDetails) || !this.hasVirtualKeyCodeMapping(e.data);

                    if (this.isAndroidOs && !imeMode) {
                        // Loop through the final composed string and send charcodes individually.
                        this.sendCharCodesAndClearText(inputText);
                    } else {
                        // Send text as unicode string
                        this.sendAndClearText(inputText);
                    }

                    if (imeMode) {
                        // indicate to client that composition has ended
                        const compositionEvent: TextCompositionEvent = { compositionText: "" };
                        this.eventEmitter.emit(EVENTS.TEXT_COMPOSITION, compositionEvent);
                    }
                }
                this.textCompositionState = TextCompositionState.COMPOSITION_NONE;

                break;
            default:
                break;
        }
    }

    private textInputHandler() {
        this.textInputDetected = true;

        let inputText = this.textInputElement!.value;
        if (this.textCompositionState == TextCompositionState.COMPOSITION_BEGIN) {
            if (this.isAndroidOs && inputText.length > 1) {
                // Bug 4137739: On some Android devices, the composition starts with multiple characters even though we process the individual keystrokes
                // The following sequence of inputs results in this behavior:
                //     input1: 5 (any numeric, does not involve composition)
                //     input2: a (any char, starts new composition)
                // Now the composing text will be '5a' instead of just 'a'. This seems to be due to the
                // system remembering the previous inputs to provide predictive suggestions.

                // When the composition just started, only consider the last input char
                inputText = inputText.charAt(inputText.length - 1);

                // However, the above breaks on Microsoft swift keyboard, as there is no way to prevent predictions.
                // On swift keyboard, even though we process individual keystrokes and clear the text, the predictions still show up
                // https://support.swiftkey.com/hc/en-us/articles/201647952-Can-I-turn-the-prediction-bar-off-on-my-Microsoft-SwiftKey-Keyboard-
                //
                // Currently we don't support 3rd party keyboards
            }
            this.textCompositionState = TextCompositionState.COMPOSITION_IN_PROGRESS;
        }
        // Note that this function will be called for every input, composing or not.
        // We process input for some cases here and for rest depend on compositionend event.

        // On Android, when isAscii=true, sendKeycodes to server irrespective of the composition state.
        // Todo: This can be generalized to all platforms and we should remove the Android check after testing on other platforms.
        if (this.isAndroidOs && this.hasVirtualKeyCodeMapping(inputText)) {
            // For ascii input, send the keycodes to server.
            this.sendCharCodesAndClearText(inputText); // If composition in progress, it gets cancelled.

            // Clearing the text does not trigger the compositionend event. Hence reset the state here
            this.textCompositionState = TextCompositionState.COMPOSITION_NONE;
            return;
        }
        // When textComposition not in progress, sendUnicode text to server. For Android, this will be non-ascii input
        // Once the Android check is removed above, we will only be sending unicode for non-ascii input.
        if (this.textCompositionState != TextCompositionState.COMPOSITION_IN_PROGRESS) {
            // When not in composition mode send unicode text to server
            this.sendAndClearText(inputText);
        }
        // When textCompositionInProgress=true and isAscii=false, compositionend event handles the input
    }

    public setVirtualKeyboardState(visible: boolean) {
        Log.i("{8bacfa5}", "{60b2d45}", visible);
        if (this.isVirtualKeyboardVisible === visible) {
            return;
        }
        this.isVirtualKeyboardVisible = visible;

        if (this.gestureDetector && this.touchListener) {
            if (this.isVirtualKeyboardVisible) {
                this.touchListener.stop();
                this.gestureDetector.start();
            } else {
                this.gestureDetector.stop();
                if (this.videoState.zoomFactor !== 1) {
                    this.applyVideoTransforms(0, 0, 1);
                }
                this.touchListener.start();
            }
        }
    }

    /** Returns true if virtual keyboard is visible, false otherwise.*/
    public getVirtualKeyboardState(): boolean {
        return this.isVirtualKeyboardVisible;
    }

    public setKeyboardLayout(layout: string) {
        if (layout == CHINESE_TRADITIONAL_LAYOUT) {
            this.tradChineseLayout = true;
        } else {
            this.tradChineseLayout = false;
        }
    }

    private getTranslationLimits(): BoundaryPair {
        return {
            horizontal: (this.videoState.displayVideoWidth * (this.videoState.zoomFactor - 1)) / 2,
            vertical: (this.videoState.displayVideoHeight * (this.videoState.zoomFactor - 1)) / 2
        };
    }

    private touchMove(target: HTMLElement, touch: TouchRecord, timestamp: number) {
        // Make sure any delayed mouse clicks happen before we move the cursor again
        this.touchDelay.flushImmediately();

        const targetRect = target.getBoundingClientRect();
        const offsetX = (touch.clientX - targetRect.left) / this.videoState.zoomFactor;
        const offsetY = (touch.clientY - targetRect.top) / this.videoState.zoomFactor;

        // Converting touch to mouse movements, so should draw the mouse cursor.
        this.setShouldDrawCursor(true);

        this.mouseHandler.setCursorPosFromOffset(offsetX, offsetY);
        const { absX, absY } = this.mouseHandler.getCursorState();
        this.packetHandler.sendCursorPos(true, absX, absY, timestamp);
    }

    private applyVideoTransforms(offsetX: number, offsetY: number, zoomFactor: number) {
        // set zoom factor first, so that translation limits are determined based on the new factor
        this.videoState.zoomFactor = zoomFactor;

        let translationLimits = this.getTranslationLimits();
        // take padding into account
        translationLimits.horizontal -= this.videoState.leftPadding;
        translationLimits.vertical -= this.videoState.topPadding;
        // allow overscroll to bring the bottom of the video to the bottom of the viewport
        // useful when the keyboard is up
        let viewportOverscroll = Math.max(
            0,
            this.videoState.displayVideoHeight -
                this.videoState.viewportHeight +
                2 * this.videoState.topPadding
        );

        let upwardLimit = Math.max(translationLimits.vertical + viewportOverscroll, 0);
        let downwardLimit = Math.max(translationLimits.vertical, 0);
        translationLimits.horizontal = Math.max(translationLimits.horizontal, 0);

        offsetX = Math.min(
            Math.max(offsetX, -1 * translationLimits.horizontal),
            translationLimits.horizontal
        );
        offsetY = Math.min(Math.max(offsetY, -1 * upwardLimit), downwardLimit);

        this.videoTagElement.style.transform = `translate3d(${offsetX}px,${offsetY}px,0px) scale3d(${zoomFactor},${zoomFactor},1)`;

        this.videoState.offsetX = offsetX;
        this.videoState.offsetY = offsetY;

        this.mouseHandler.updateVideoState(this.videoState);
    }

    public clientRequestVideoTransform(offsetX: number, offsetY: number, zoomFactor: number) {
        // update the viewport in case the client is making the request on a viewport resize, and we haven't been notified yet
        const viewport = (<any>window).visualViewport;
        if (viewport) {
            this.videoState.viewportHeight = viewport.height;
        }
        this.applyVideoTransforms(offsetX, offsetY, zoomFactor);
    }

    shouldPreventDefaultTouch(): boolean {
        // This is called for touch events on the video element, so we can reset the idle timeout.
        // Touch events outside the video element use the idleInputListenerFunc to clear the idle timeout.
        if (this.isUserIdleTimeoutPending) {
            this.clearIdleTimeout();
            return false;
        }
        // keyboard state does not correctly track when using a floating or split keyboard
        // if that is fixed, we can call preventDefault only when the keyboard is up to keep the text element in focus
        // return this.isVirtualKeyboardVisible;
        // let browser restore focus
        return this.deviceObserver.getWindowFocus();
    }

    shouldPreventDefaultKb(evt: KeyboardEvent): boolean {
        if (this.streamClient.getLdatHandler()?.isVisible()) {
            return false;
        }
        if (this.isChromeOs) {
            switch (evt.code) {
                case "Tab":
                    return evt.altKey ? false : true;
                case "ZoomToggle":
                case "SelectTask":
                case "BrightnessDown":
                case "BrightnessUp":
                case "AudioVolumeMute":
                case "AudioVolumeDown":
                case "AudioVolumeUp":
                    return false;
            }
        }

        return true;
    }

    private clearStatsGestureTimer(): void {
        window.clearTimeout(this.statsGestureTimerId);
        this.statsGestureTimerId = 0;
        this.twoFingerTapCount = 0;
        this.threeFingerTapCount = 0;
    }

    tap(target: HTMLElement, timestamp: number, lastTouch: TouchRecord, touchCount: number) {
        this.touchDelay.flushImmediately();
        switch (touchCount) {
            case 1:
                this.touchMove(target, lastTouch, timestamp);
                this.touchDelay.delay(() => {
                    this.emulateMouseClick(MouseButtons.LEFT_CLICK, timestamp);
                });
                break;
            case 2:
                this.twoFingerTapCount++;
                if (this.statsGestureTimerId === 0) {
                    this.statsGestureTimerId = window.setTimeout(() => {
                        if (this.twoFingerTapCount === 2) {
                            this.streamClient.toggleOnScreenStats();
                        } else if (this.twoFingerTapCount === 3) {
                            this.streamClient.toggleOnScreenStats(RagnarokSettings.isInternalUser);
                        }
                        this.clearStatsGestureTimer();
                    }, 300);
                }
                this.emulateMouseClick(MouseButtons.RIGHT_CLICK, timestamp);
                break;
            case 3:
                this.threeFingerTapCount++;
                if (RagnarokSettings.isInternalUser && this.statsGestureTimerId === 0) {
                    this.statsGestureTimerId = window.setTimeout(() => {
                        if (this.threeFingerTapCount === 2) {
                            this.streamClient.sendQosMarker();
                        } else if (this.threeFingerTapCount === 3) {
                            this.safeZoneHandler.toggleDisplaySafeZone();
                        }
                        this.clearStatsGestureTimer();
                    }, 300);
                }
                this.emulateMouseClick(MouseButtons.MIDDLE_CLICK, timestamp);

                break;
            case 4:
                this.emulateMouseClick(MouseButtons.MOUSE_FOUR, timestamp);
                break;
            case 5:
                this.emulateMouseClick(MouseButtons.MOUSE_FIVE, timestamp);
                break;
        }
    }

    private emulateMouseClick(button: MouseButtons, timestamp: number) {
        this.sendMouseDown(button, timestamp);
        this.touchDelay.delay(() => {
            this.sendMouseUp(button, timestamp);
        });
    }

    holdBegin(target: HTMLElement, timestamp: number, touch: TouchRecord) {
        if (this.videoState.zoomFactor === 1) {
            this.touchMove(target, touch, timestamp);
            this.touchDelay.delay(() => {
                this.sendMouseDown(MouseButtons.LEFT_CLICK, timestamp);
            });
        }
    }

    holdEnd(target: HTMLElement, timestamp: number) {
        if (this.videoState.zoomFactor === 1) {
            // Make sure the mouse down from holdBegin is sent before the up
            this.touchDelay.flushImmediately();
            this.sendMouseUp(MouseButtons.LEFT_CLICK, timestamp);
        }
    }

    drag(target: HTMLElement, timestamp: number, touch: TouchRecord) {
        // Drag gesture should pan if part of the video is not visible or streaming a true touch app
        if (
            this.videoState.zoomFactor !== 1 ||
            this.isVirtualKeyboardVisible ||
            this.touchListener !== undefined
        ) {
            // pan
            this.applyVideoTransforms(
                this.videoState.offsetX + touch.deltaX,
                this.videoState.offsetY + touch.deltaY,
                this.videoState.zoomFactor
            );
        } else {
            // mouse move
            this.touchMove(target, touch, timestamp);
        }
    }

    scroll(target: HTMLElement, timestamp: number, touches: TouchRecord[]) {
        if (touches.length > 0) {
            this.packetHandler.sendMouseWheel(Math.sign(touches[0].deltaY), timestamp); // TODO: translate magnitude of drag to magnitude of the scroll
        }
    }

    panZoom(target: HTMLElement, timestamp: number, touches: TouchRecord[]) {
        if (touches.length === 2) {
            this.mouseHandler.setZoomInProgress(true);

            const firstTouch = touches[0];
            const secondTouch = touches[1];

            const currentDistance = Math.hypot(
                firstTouch.clientX - secondTouch.clientX,
                firstTouch.clientY - secondTouch.clientY
            );
            const previousDistance = Math.hypot(
                firstTouch.clientX - firstTouch.deltaX - (secondTouch.clientX - secondTouch.deltaX),
                firstTouch.clientY - firstTouch.deltaY - (secondTouch.clientY - secondTouch.deltaY)
            );

            const bounding = this.videoTagElement.getBoundingClientRect();
            const translationLimits = this.getTranslationLimits();

            let zoomFactor = this.videoState.zoomFactor * (currentDistance / previousDistance);
            zoomFactor = Math.min(Math.max(zoomFactor, 1), MAX_ZOOM_FACTOR);

            let offsetX = (firstTouch.clientX + secondTouch.clientX) / 2 - bounding.left; // touch center
            offsetX +=
                this.videoState.offsetX -
                translationLimits.horizontal -
                (bounding.width - offsetX) / this.videoState.zoomFactor; // relative offset of touch center from video center
            offsetX = (zoomFactor / this.videoState.zoomFactor - 1) * offsetX * -1; // scale offset
            offsetX += this.videoState.offsetX + (firstTouch.deltaX + secondTouch.deltaX) / 2; // add pan distance

            let offsetY = (firstTouch.clientY + secondTouch.clientY) / 2 - bounding.top; // touch center
            offsetY +=
                this.videoState.offsetY -
                translationLimits.vertical -
                (bounding.height - offsetY) / this.videoState.zoomFactor; // relative offset of touch center from video center
            offsetY = (zoomFactor / this.videoState.zoomFactor - 1) * offsetY * -1; // scale offset
            offsetY += this.videoState.offsetY + (firstTouch.deltaY + secondTouch.deltaY) / 2; // add pan distance

            this.applyVideoTransforms(offsetX, offsetY, zoomFactor);
        }
    }

    panZoomEnd(target: HTMLElement, timestamp: number) {
        this.mouseHandler.setZoomInProgress(false);
        if (this.videoState.zoomFactor < MIN_ZOOM_FACTOR) {
            this.applyVideoTransforms(this.videoState.offsetX, this.videoState.offsetY, 1);
        }
    }

    private getMargins(): BoundaryPair {
        const boundingRect = this.videoTagElement.getBoundingClientRect();
        return {
            horizontal:
                boundingRect.left +
                window.pageXOffset +
                this.videoState.leftPadding * this.videoState.zoomFactor,
            vertical:
                boundingRect.top +
                window.pageYOffset +
                this.videoState.topPadding * this.videoState.zoomFactor
        };
    }

    /**
     * If keyboard input is kept enabled when the GFN virtual keyboard is on,
     * inputs meant to navigate it will affect the stream unintentionally.
     * So utilize keydown/keyup methods for GFN virtual Keyboard input only.
     */
    public sendKeyEvent(event: KeyboardEvent) {
        // If we get here before the input channel is open, keydown and keyup can affect our internal state but we won't
        // be able to send the key press packets. Just bail early if we hit this
        if (!this.packetHandler.channelsOpen()) {
            return;
        }
        const keyCode = this.getVirtualKeycode(event);
        if (keyCode) {
            if (event.type === "keydown") {
                this.keydown(event);
            } else if (event.type === "keyup") {
                this.keyup(event);
            }
        } else if (event.type === "keydown" && event.key.length === 1) {
            // For symbols that are not in virtual key list. eg. £, €, ™, ©, ¢, etc.
            const encoder = new TextEncoder();
            const unicodeText = encoder.encode(event.key);
            this.sendTextInput(unicodeText.buffer);
        }
    }

    private sendSafeZone(): void {
        this.clearSafeZoneTimeout();

        // Use 350ms delay before notifying safezone handler to ensure any orientation change is detectable
        this.safeZoneTimeoutId = window.setTimeout(() => {
            this.safeZoneHandler.send();
        }, 350);
    }

    private clearSafeZoneTimeout(): void {
        if (this.safeZoneTimeoutId !== 0) {
            window.clearTimeout(this.safeZoneTimeoutId);
            this.safeZoneTimeoutId = 0;
        }
    }

    private sendMouseDown(button: number, ts: number) {
        this.mouseHandler.sendMouseDown(button, ts);
    }

    private sendMouseUp(button: number, ts: number) {
        this.mouseHandler.sendMouseUp(button, ts);
    }

    public onSendInput(): boolean {
        if (this.isUserIdleTimeoutPending) {
            this.clearIdleTimeout();
            return false;
        }
        return true;
    }

    public getInputMode(): string {
        return this.mouseHandler.getMouseMode();
    }

    public setShouldDrawCursor(draw: boolean) {
        this.mouseHandler.setShouldDrawCursor(draw);
    }
}

// This class only exists because some games/apps don't handle mouse moves and clicks immediately after one another.
// It's an app bug but we still have to deal with it. Basically, this does a best-effort delay on a given event. To
// preserve ordering, if another event comes before the previous delay elapses, we give up and call the previous
// callback early. This class is pretty hacky to support chained delay() calls, needed for tap (move -> down -> up all
// need delays)
class TouchDelay {
    static TOUCH_DELAY_MS: number = 30;

    private timerId: number = 0;
    private callback?: VoidFunction;
    private flushing: boolean = false;

    // Immediately calls the currently-delayed input callback, if any
    // Any calls to delay() during the flush will complete immediately
    public flushImmediately() {
        this.flushing = true;
        this.resetTimer();
        this.callCallback();
        this.flushing = false;
    }

    // Clears the currently-delayed input callback without calling it
    public clear() {
        this.callback = undefined;
        this.resetTimer();
    }

    // Schedules the given callback to be called in TOUCH_DELAY_MS
    // Only delay() can be called from within a callback. Calling other members can cause unexpected behavior
    public delay(callback: VoidFunction) {
        if (this.flushing) {
            callback();
            return;
        }
        this.flushImmediately();

        this.callback = callback;
        this.timerId = window.setTimeout(() => {
            this.timerId = 0;
            this.callCallback();
        }, TouchDelay.TOUCH_DELAY_MS);
    }

    private callCallback() {
        // Set this.callback to undefined before calling it in case it calls delay() with a new callback
        const callback = this.callback;
        if (callback) {
            this.callback = undefined;
            callback();
        }
    }

    private resetTimer() {
        if (this.timerId !== 0) {
            window.clearTimeout(this.timerId);
            this.timerId = 0;
        }
    }
}

function getModifierKeysFromInputFlags(flag: number): VIRTUAL_KEYS[] {
    let keys = [];
    if (flag & InputModifierFlags.NVST_MF_CONTROL) {
        keys.push(VIRTUAL_KEYS.CODE_LCONTROL);
        keys.push(VIRTUAL_KEYS.CODE_RCONTROL);
    }
    if (flag & InputModifierFlags.NVST_MF_ALT) {
        keys.push(VIRTUAL_KEYS.CODE_LMENU);
        keys.push(VIRTUAL_KEYS.CODE_RMENU);
    }
    if (flag & InputModifierFlags.NVST_MF_META) {
        keys.push(VIRTUAL_KEYS.CODE_LWIN);
        keys.push(VIRTUAL_KEYS.CODE_RWIN);
    }
    if (flag & InputModifierFlags.NVST_MF_SHIFT) {
        keys.push(VIRTUAL_KEYS.CODE_LSHIFT);
        keys.push(VIRTUAL_KEYS.CODE_RSHIFT);
    }

    return keys;
}

function getModifierKeysFromModifierFlags(modifiers: HotkeyModifier): VIRTUAL_KEYS[] {
    let keys: VIRTUAL_KEYS[] = [];
    if (modifiers & HotkeyModifier.Ctrl) {
        keys.push(VIRTUAL_KEYS.CODE_LCONTROL);
    }
    if (modifiers & HotkeyModifier.Alt) {
        keys.push(VIRTUAL_KEYS.CODE_LMENU);
    }
    if (modifiers & HotkeyModifier.Meta) {
        keys.push(VIRTUAL_KEYS.CODE_LWIN);
    }
    if (modifiers & HotkeyModifier.Shift) {
        keys.push(VIRTUAL_KEYS.CODE_LSHIFT);
    }

    return keys;
}

function getModifierFlagFromKey(key: VIRTUAL_KEYS): number {
    switch (key) {
        case VIRTUAL_KEYS.CODE_LCONTROL:
        case VIRTUAL_KEYS.CODE_RCONTROL:
            return InputModifierFlags.NVST_MF_CONTROL;
        case VIRTUAL_KEYS.CODE_LMENU:
        case VIRTUAL_KEYS.CODE_RMENU:
            return InputModifierFlags.NVST_MF_ALT;
        case VIRTUAL_KEYS.CODE_LWIN:
        case VIRTUAL_KEYS.CODE_RWIN:
            return InputModifierFlags.NVST_MF_META;
        case VIRTUAL_KEYS.CODE_LSHIFT:
        case VIRTUAL_KEYS.CODE_RSHIFT:
            return InputModifierFlags.NVST_MF_SHIFT;
    }
    return 0;
}
